import React, { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { filtrarBastidores, acessorios, ncbIncluidos, categoriaLabels, Acessorio } from '@/data/index';
import { useI18n } from '@/lib/i18n';
import Layout from '@/components/Layout';
import {
  ArrowLeft, Copy, Check, AlertCircle, ChevronLeft, ChevronRight,
  Network, LayoutIcon, Package, Wind, Plug, Plus, Minus, ExternalLink
} from 'lucide-react';

const catIcons: Record<Acessorio['categoria'], React.ReactNode> = {
  painel_distribuicao: <Network className="w-3.5 h-3.5" />,
  gestao_cabos: <LayoutIcon className="w-3.5 h-3.5" />,
  prateleira: <Package className="w-3.5 h-3.5" />,
  ventilacao: <Wind className="w-3.5 h-3.5" />,
  pdu: <Plug className="w-3.5 h-3.5" />,
};

const tipoLabel: Record<string, string> = {
  NCB: 'Bastidor de Chão NCB',
  RACK_MURAL_PROX: 'Rack Mural ProX',
  SLIMX: 'SlimX DVR Rack Mural',
};

export default function Resultado() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();

  const filtros = useMemo(() => ({
    capacidadeU: params.get('u') ? Number(params.get('u')) : undefined,
    larguraMM: params.get('l') ? Number(params.get('l')) : undefined,
    profundidadeMM: params.get('p') ? Number(params.get('p')) : undefined,
    alturaMM: params.get('a') ? Number(params.get('a')) : undefined,
  }), [params]);

  const resultados = useMemo(() => filtrarBastidores(filtros), [filtros]);
  const [idx, setIdx] = useState(0);
  const item = resultados[idx] ?? null;
  const bastidor = item?.bastidor ?? null;
  const [qtds, setQtds] = useState<Record<string, number>>({});
  const [copiado, setCopiado] = useState(false);

  const alterarQtd = (ref: string, delta: number) => {
    setQtds(prev => {
      const atual = prev[ref] ?? 0;
      const novo = Math.max(0, atual + delta);
      if (novo === 0) { const { [ref]: _, ...rest } = prev; return rest; }
      return { ...prev, [ref]: novo };
    });
  };

  const selecionados = acessorios.filter(a => (qtds[a.ref] ?? 0) > 0).map(a => ({ ...a, quantidade: qtds[a.ref] }));
  const capU = bastidor && 'capacidadeU' in bastidor ? bastidor.capacidadeU : null;
  const uUsadas = selecionados.reduce((s, a) => s + a.quantidade, 0);
  const uLivres = capU !== null ? capU - uUsadas : null;
  const excede = uLivres !== null && uLivres < 0;

  // Filtrar acessórios compatíveis com a profundidade do bastidor selecionado
  const profBastidor = bastidor?.profundidadeMM ?? 0;
  const acessoriosCompativeis = acessorios.filter(a =>
    !a.minProfundidadeMM || profBastidor >= a.minProfundidadeMM
  );

  const categorias = [...new Set(acessoriosCompativeis.map(a => a.categoria))];

  const copiar = () => {
    const linhas: string[] = [];
    if (bastidor) {
      linhas.push(`${bastidor.refInterna} — ${bastidor.descricao}`);
      if (bastidor.tipo === 'NCB') linhas.push('  Incluído: ' + ncbIncluidos.join(', '));
    }
    selecionados.forEach(a => linhas.push(`${a.ref} × ${a.quantidade} — ${a.nome} — ${a.descricao}`));
    navigator.clipboard.writeText(linhas.join('\n')).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  };

  if (resultados.length === 0) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center py-24 px-5">
          <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-muted-foreground" />
          </div>
          <h2 className="text-lg font-bold mb-2">{t('semResultado')}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('semResultadoSub')}</p>
          <button
            onClick={() => navigate(ROUTE_PATHS.HOME)}
            className="inline-flex items-center gap-2 px-5 h-10 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t('voltar')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 py-8">

        {/* Voltar */}
        <button
          onClick={() => navigate(ROUTE_PATHS.HOME)}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {t('voltar')}
        </button>

        {/* Título resultados */}
        <div className="mb-6">
          <h1 className="text-xl font-bold text-foreground">{t('resultadosTitle')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t('resultadosSub')}</p>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">

          {/* Coluna principal */}
          <div className="space-y-5">

            {/* Card bastidor */}
            {bastidor && (
              <div className="bg-white border border-border/70 rounded-2xl overflow-hidden shadow-sm">
                {/* Topo colorido */}
                <div className="bg-primary/5 border-b border-border/50 px-6 py-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-primary mb-1">
                      {tipoLabel[bastidor.tipo] ?? bastidor.tipo}
                    </p>
                    <h2 className="text-base font-bold text-foreground leading-snug">{bastidor.descricao}</h2>
                    <p className="text-[11px] font-mono text-muted-foreground mt-1">{bastidor.refInterna}</p>
                  </div>
                  {bastidor.pvp && (
                    <div className="shrink-0 text-right">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('pvp')}</p>
                      <p className="text-lg font-bold text-foreground">{bastidor.pvp.toFixed(2)} €</p>
                    </div>
                  )}
                </div>

                {/* Especificações */}
                <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {capU !== null && (
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">{t('capacidade')}</p>
                      <p className="text-sm font-bold text-foreground">{capU}U</p>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">{t('larguraMm')}</p>
                    <p className="text-sm font-bold text-foreground">{bastidor.larguraMM} mm</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">{t('profundidadeMm')}</p>
                    <p className="text-sm font-bold text-foreground">{bastidor.profundidadeMM} mm</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">{t('alturaMm')}</p>
                    <p className="text-sm font-bold text-foreground">{bastidor.alturaMM} mm</p>
                  </div>
                </div>

                {/* Incluído (NCB) */}
                {bastidor.tipo === 'NCB' && (
                  <div className="px-6 py-3 bg-emerald-50/60 border-t border-emerald-100">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 mb-1">✔ {t('incluido')}</p>
                    <p className="text-[11px] text-emerald-800 leading-relaxed">{ncbIncluidos.join(' · ')}</p>
                  </div>
                )}

                {/* ProX msg */}
                {bastidor.tipo === 'RACK_MURAL_PROX' && (
                  <div className="px-6 py-3 bg-amber-50/60 border-t border-amber-100">
                    <p className="text-[11px] text-amber-800">★ {t('proxMsg')}</p>
                  </div>
                )}

                {/* Ocupação visual */}
                {capU !== null && (
                  <div className="px-6 py-4 border-t border-border/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{t('ocupacaoVisual')}</p>
                      <div className="flex items-center gap-3 text-[11px]">
                        <span className="text-muted-foreground">{uUsadas} {t('uUsadas')}</span>
                        <span className={excede ? 'font-bold text-destructive' : 'font-semibold text-emerald-600'}>
                          {excede ? t('excede') : `${uLivres} ${t('uLivres')}`}
                        </span>
                      </div>
                    </div>
                    <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${excede ? 'bg-destructive' : 'bg-primary'}`}
                        style={{ width: `${Math.min(100, (uUsadas / capU) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Ver produto */}
                <div className="px-6 pb-4">
                  <a
                    href={bastidor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:underline"
                  >
                    {t('verProduto')} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Paginação */}
            {resultados.length > 1 && (
              <div className="flex items-center justify-between bg-white border border-border/70 rounded-xl px-5 py-3 shadow-sm">
                <button
                  onClick={() => { setIdx(i => Math.max(0, i - 1)); setQtds({}); }}
                  disabled={idx === 0}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> {t('previo')}
                </button>
                <span className="text-sm font-semibold text-foreground">
                  {idx + 1} <span className="text-muted-foreground font-normal">{t('de')}</span> {resultados.length}
                </span>
                <button
                  onClick={() => { setIdx(i => Math.min(resultados.length - 1, i + 1)); setQtds({}); }}
                  disabled={idx === resultados.length - 1}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t('seguinte')} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Acessórios (só NCB/ProX) */}
            {bastidor && bastidor.tipo !== 'SLIMX' && (
              <div className="bg-white border border-border/70 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-border/50">
                  <h3 className="text-sm font-bold text-foreground">{t('acessorios')}</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t('acessoriosSub')}</p>
                </div>
                <div className="divide-y divide-border/40">
                  {categorias.map(cat => (
                    <div key={cat}>
                      <div className="px-6 py-2 bg-muted/40 flex items-center gap-2">
                        <span className="text-muted-foreground">{catIcons[cat]}</span>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{categoriaLabels[cat]}</p>
                      </div>
                      {acessoriosCompativeis.filter(a => a.categoria === cat).map(ac => {
                        const q = qtds[ac.ref] ?? 0;
                        return (
                          <div key={ac.ref} className="px-6 py-3 flex items-center gap-3 hover:bg-muted/20 transition-colors">
                            <img
                              src={ac.imagemUrl}
                              alt={ac.nome}
                              className="w-10 h-10 object-cover rounded-lg border border-border/50 shrink-0 bg-muted"
                              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-semibold text-foreground truncate">{ac.nome}</p>
                              <p className="text-[10px] text-muted-foreground font-mono">{ac.ref}</p>
                            </div>
                            {ac.pvp && (
                              <p className="text-[11px] font-semibold text-foreground shrink-0">{ac.pvp.toFixed(2)} €</p>
                            )}
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => alterarQtd(ac.ref, -1)}
                                disabled={q === 0}
                                className="w-7 h-7 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-6 text-center text-sm font-bold text-foreground">{q}</span>
                              <button
                                onClick={() => alterarQtd(ac.ref, 1)}
                                className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — Resumo */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white border border-border/70 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-border/50 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground">{t('resumo')}</h3>
                <button
                  onClick={copiar}
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition-all ${
                    copiado
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                      : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-border/80'
                  }`}
                >
                  {copiado ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiado ? t('copiado') : t('copiar')}
                </button>
              </div>
              <div className="px-5 py-4 space-y-3">
                {bastidor && (
                  <div className="rounded-xl bg-primary/5 border border-primary/15 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-primary mb-1">{tipoLabel[bastidor.tipo]}</p>
                    <p className="text-[11px] font-semibold text-foreground leading-snug">{bastidor.descricao}</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-1">{bastidor.refInterna}</p>
                  </div>
                )}
                {selecionados.length === 0 && (
                  <p className="text-[11px] text-muted-foreground text-center py-4">—</p>
                )}
                {selecionados.map(a => (
                  <div key={a.ref} className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-foreground leading-snug truncate">{a.nome}</p>
                      <p className="text-[10px] font-mono text-muted-foreground">{a.ref}</p>
                    </div>
                    <span className="shrink-0 text-[11px] font-bold text-foreground bg-muted rounded-full px-2 py-0.5">×{a.quantidade}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
